import React, {useCallback, useRef, useState} from 'react';

import {Button, Panel, useSmUp} from '@pexip/components';
import type {ParticipantID} from '@pexip/infinity';

import type {
    BreakoutParticipants,
    BreakoutRoomNames,
    BreakoutsEditPanelSave,
} from '../../types';
import {BreakoutRoomVariant} from '../../types';
import {PanelHeader} from '../PanelHeader/PanelHeader.view';
import {BreakoutRoom} from '../BreakoutRoom/BreakoutRoom.view';
import {TestId} from '../../../test/testIds';
import {
    useBreakoutParticipantDrag,
    useBreakoutChangeParticipantRoom,
    useBreakoutRemoveRoom,
    useBreakoutRoomNameChange,
    useBreakoutAddRoom,
    useBreakoutShuffleParticipants,
} from '../../hooks';
import {useBreakoutEditDiff} from '../../hooks/useBreakoutEditDiff';
import {BreakoutRoomsConfigurationMenu} from '../BreakoutRoomsConfiguration/BreakoutRoomsConfiguration.view';

import styles from './BreakoutEditPanel.module.scss';

export const BreakoutEditPanel: React.FC<{
    currentParticipants: BreakoutParticipants;
    myIdentity?: ParticipantID;
    breakoutRoomsNames: BreakoutRoomNames;
    saveButtonText: string;
    cancelButtonText: string;
    headerText?: string;
    onSave: (data: BreakoutsEditPanelSave) => void;
    onCancel: () => void;
    onClosePanel: () => void;
    onSettingsButtonClick?: () => void;
}> = ({
    currentParticipants,
    myIdentity,
    breakoutRoomsNames: breakoutRoomsNamesProp,
    saveButtonText,
    cancelButtonText,
    headerText,
    onSave,
    onCancel,
    onClosePanel,
    onSettingsButtonClick,
}) => {
    const isSmUp = useSmUp();
    const [participants, setParticipants] = useState(currentParticipants);
    const [breakoutRoomsNames, setBreakoutRoomsNames] = useState(
        breakoutRoomsNamesProp,
    );

    const [isDragging, setIsDragging] = useState(false);

    const scrollElementRef = useRef<HTMLElement>(null);

    const changeParticipantRoom = useBreakoutChangeParticipantRoom({
        participants,
        setParticipants,
    });

    const {
        targetRoomId: currentDragTarget,
        onParticipantDragging,
        onParticipantDrag,
        getBreakoutRoomRefCallback,
    } = useBreakoutParticipantDrag({
        participants,
        changeParticipantRoom,
        setIsDragging,
    });

    const onRemoveRoom = useBreakoutRemoveRoom({setParticipants});

    const {handleBreakoutRoomNameChange} = useBreakoutRoomNameChange({
        setParticipants,
        participants,
        breakoutRoomsNames,
        setBreakoutRoomsNames,
    });

    const onAddRoom = useBreakoutAddRoom({
        setParticipants,
        existingBreakoutNames: Object.values(breakoutRoomsNames),
    });

    const {doShuffle, isShuffling} = useBreakoutShuffleParticipants({
        setParticipants,
        myIdentity,
    });

    const reduceToIdentities = (participants: BreakoutParticipants) =>
        Array.from(participants).reduce((acc, [roomId, roomParticipants]) => {
            acc.set(
                roomId,
                roomParticipants.map(p => p.uuid),
            );
            return acc;
        }, new Map());

    const resolveEdit = useBreakoutEditDiff({
        liveBreakoutNames: breakoutRoomsNamesProp,
        localBreakoutNames: breakoutRoomsNames,
        liveParticipants: reduceToIdentities(currentParticipants),
        localParticipants: reduceToIdentities(participants),
    });

    const doSave = useCallback(() => {
        const updateActions = resolveEdit();
        onSave(updateActions);
    }, [resolveEdit, onSave]);

    return (
        <Panel
            scrollElementRef={scrollElementRef}
            overflowHidden={isDragging}
            isRounded={isSmUp}
            padding="none"
            footerPadding="small"
            headerContent={
                <PanelHeader
                    title={headerText}
                    onCloseClick={onClosePanel}
                    closeButtonTestId={TestId.PanelHeaderCloseButton}
                    endContent={
                        <BreakoutRoomsConfigurationMenu
                            onAddRoomButtonClick={onAddRoom}
                            onSettingsButtonClick={onSettingsButtonClick}
                            onShuffledButtonClick={doShuffle}
                            isShuffling={isShuffling}
                        />
                    }
                />
            }
            footerContent={
                <div className={styles.footer}>
                    <Button
                        variant="secondary"
                        modifier="fullWidth"
                        onClick={onCancel}
                    >
                        {cancelButtonText}
                    </Button>
                    <Button
                        modifier="fullWidth"
                        onClick={doSave}
                        data-testid={TestId.ButtonSaveBreakoutRoomsEdit}
                    >
                        {saveButtonText}
                    </Button>
                </div>
            }
            scrollElementClassname="pt-2 pr-3 pb-3 pl-3"
        >
            {Array.from(participants.entries()).map(
                ([roomId, roomParticipants]) => {
                    return (
                        <BreakoutRoom
                            scrollElementRef={scrollElementRef}
                            variant={BreakoutRoomVariant.Edit}
                            id={roomId}
                            key={roomId}
                            name={breakoutRoomsNames.get(roomId) ?? roomId}
                            rooms={Array.from(participants.keys()).reduce(
                                (acc, current) => {
                                    if (roomId !== current) {
                                        acc[current] =
                                            breakoutRoomsNames.get(current) ??
                                            current;
                                    }
                                    return acc;
                                },
                                {} as Record<string, string>,
                            )}
                            myIdentity={myIdentity}
                            participants={roomParticipants}
                            isDragTarget={currentDragTarget === roomId}
                            onNameChanged={handleBreakoutRoomNameChange}
                            onParticipantDragging={onParticipantDragging}
                            onParticipantDrag={onParticipantDrag}
                            onParticipantRoomChange={changeParticipantRoom}
                            onRemoveRoom={onRemoveRoom}
                            ref={getBreakoutRoomRefCallback(roomId)}
                            canEditRoomName={!breakoutRoomsNames.get(roomId)}
                        />
                    );
                },
            )}
        </Panel>
    );
};

export type BreakoutEditPanelProps = React.ComponentProps<
    typeof BreakoutEditPanel
>;
